/**
 * ActivityArchivedRow — the Activity layout's archived-shelf row.
 *
 * One line, ported from t3code's `SidebarThreadRow` `variant="slim"`:
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ ▣  Fix the relay handshake      #482     4h  │   at rest
 *   │ ▣  Fix the relay handshake      #482      ↺  │   on hover
 *   └──────────────────────────────────────────────┘
 *
 * Serves two overlapping lifecycles on the same shelf: t3code's *settled*
 * (`effectiveSettled`, explicit override or auto-settle-by-silence) and
 * Forge's own *archived* (`thread.archivedAt`, storage-released). An archived
 * thread is always settled; settled does not imply archived. Same row shape
 * either way — history that stays findable without competing with live
 * work — but the restore action differs, see `onRestore`. Snooze is not
 * ported.
 *
 * **Why this is not a `variant` prop on `ActivityThreadCard`.** Same reason
 * that card is not a variant of `SidebarThreadRow`, stated in its header: the
 * two answer different questions. A live card carries its project, branch, PR
 * and status because you are deciding what to do next. An archived row is
 * scanned, not decided on, so it carries only what you scan by — icon, title,
 * PR number, age — and spends the rest of its width on nothing, which is the
 * point. What they *do* share is the surface
 * (`resolveActivityRowSurfaceClassName`) and every behaviour: both go through
 * `useSidebarThreadRow`, so selection, range-select, the context menu, rename
 * and PR opening are one implementation, not two.
 *
 * **Metrics go through `--acard`, never literal Tailwind rem utilities.**
 * `--acard` is `1rem` and every multiplier below is the reference design's own
 * rem value, so the row keeps its drawn proportions and still tracks the
 * user's interface-size setting. It is a custom property rather than `em`
 * because `em` compounds; `ActivityThreadCard` carries the full derivation.
 *
 * **Every `text-[length:…]` is paired with an explicit `leading-…`.** The
 * arbitrary form sets font-size alone, so the leading falls through to
 * preflight's 1.5 ratio and the line grows past the row's fixed height.
 *
 * **Hover state hangs off `group/menu-sub-item`, not t3code's
 * `group/sidebar-row`.** `SidebarMenuSubItem` is the `<li>` Forge sidebar rows
 * render into and it owns that group name (`ThreadListShell` documents the
 * contract). A verbatim port of upstream's group names compiles to nothing: the
 * row would look right at rest and never react to a pointer.
 */

import { TerminalIcon } from "lucide-react";
import type { ScopedThreadRef } from "@cmmd-forge/contracts";

import { ProjectIcon } from "../ProjectIcon";
import { useProjectIconResolver } from "../projects/useProjectIconResolver";
import { SidebarMenuSubItem } from "../ui/sidebar";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { cn } from "~/lib/utils";
import { useSidebarThreadRow } from "./useSidebarThreadRow";
import {
  type ActivityCardEmphasisModel,
  resolveActivityCardEmphasis,
  resolveActivityCardPrBadge,
  resolveActivityRowSurfaceClassName,
  resolveArchivedRowPrHoverClass,
} from "./activityThreadCard.logic";
import { ActivityThreadCardTooltip } from "./ActivityThreadCardTooltip";
import { resolveLocalCheckoutBranchMismatch } from "../BranchToolbar.logic";
import { resolveSidebarThreadIdentity } from "./SidebarThreadRow";
import { SidebarThreadJumpHint } from "./SidebarThreadRowBadges";
import type { ActivityThreadCardProps } from "./ActivityThreadCard";
import { ActivityArchivedRowAgeSlot } from "./ActivityArchivedRowAgeSlot";

type ActivityArchivedRowState = ReturnType<typeof useSidebarThreadRow>;

export interface ActivityArchivedRowProps extends ActivityThreadCardProps {
  /**
   * Returns the thread to the live list. The way out for the way in.
   *
   * Resolved by the caller, not this row: a row here is either archived
   * (`thread.archivedAt`) or merely settled, and the two need different
   * mutations — `unarchiveThread` does not clear `settledAt`/`settledOverride`,
   * so calling it on a settled-not-archived thread would leave the row stuck on
   * the shelf it just "left". The caller already has `thread.archivedAt` in
   * scope when it builds this prop, so the branch lives there once rather than
   * inside every row.
   */
  readonly onRestore: (threadRef: ScopedThreadRef) => void;
}

// ---------------------------------------------------------------------------
// Project mark
// ---------------------------------------------------------------------------

/**
 * Its own component so the identity-resolving hook is not added to the row's
 * layout. Same ladder the card uses: an explicit `projectIcon` (cloud rows), a
 * CMMD record reached through the project link, then the checkout's favicon.
 */
function ArchivedRowProjectMark(props: { readonly rowProps: ActivityArchivedRowProps }) {
  const resolveProjectIcon = useProjectIconResolver();
  const { rowProps } = props;
  return (
    <ProjectIcon
      environmentId={rowProps.thread.environmentId}
      cwd={rowProps.projectCwd ?? ""}
      projectName={rowProps.projectName}
      canonicalIdentity={
        rowProps.projectIcon ??
        resolveProjectIcon(rowProps.thread.environmentId, rowProps.thread.projectId)
      }
      className="size-[var(--acard)] shrink-0"
    />
  );
}

// ---------------------------------------------------------------------------
// Title
// ---------------------------------------------------------------------------

/**
 * t3code's slim tone ramp, deliberately quieter than the card's: an archived
 * row that finished while you were away is `muted-foreground` rather than the
 * card's full `foreground`, because "you have not seen this yet" matters less
 * once the thread is done than it does while it is live.
 */
function resolveArchivedTitleToneClass(input: {
  readonly isActive: boolean;
  readonly isUnread: boolean;
}): string {
  if (input.isActive) return "text-foreground";
  return input.isUnread ? "text-muted-foreground" : "text-secondary-label/70";
}

function ActivityArchivedRowTitle(props: {
  readonly rowProps: ActivityArchivedRowProps;
  readonly state: ActivityArchivedRowState;
  readonly emphasis: ActivityCardEmphasisModel;
}) {
  const { rowProps, state, emphasis } = props;

  if (state.renamingThreadKey === state.threadKey) {
    return (
      <input
        ref={state.handleRenameInputRef}
        aria-label="Thread title"
        // Height, not leading alone: the input carries a 1px border, so a bare
        // line box would make the control taller than the title it replaces
        // inside a row whose height is fixed. `border-box` folds the border in.
        className="h-[calc(var(--acard)*1.25)] min-w-0 flex-1 rounded-sm border border-input bg-card px-[calc(var(--acard)*0.25)] text-[length:calc(var(--acard)*0.875)] leading-[calc(var(--acard)*1.25)] font-medium text-card-foreground outline-none focus:border-foreground"
        value={rowProps.renamingTitle}
        onChange={state.handleRenameInputChange}
        onKeyDown={state.handleRenameInputKeyDown}
        onBlur={state.handleRenameInputBlur}
        onClick={state.handleRenameInputClick}
      />
    );
  }

  return (
    <span
      data-testid={`thread-title-${rowProps.thread.id}`}
      className={cn(
        "min-w-0 flex-1 truncate text-[length:calc(var(--acard)*0.875)] leading-[calc(var(--acard)*1.25)] transition-colors motion-reduce:transition-none group-hover/menu-sub-item:text-foreground",
        emphasis.shouldRecede ? "font-normal" : "font-medium",
        resolveArchivedTitleToneClass({
          isActive: rowProps.isActive,
          isUnread: emphasis.isUnread,
        }),
      )}
    >
      {resolveSidebarThreadIdentity(rowProps.thread).title}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Terminal glyph and PR badge
// ---------------------------------------------------------------------------

function ActivityArchivedRowTerminalGlyph(props: { readonly state: ActivityArchivedRowState }) {
  const terminalStatus = props.state.terminalStatus;
  if (!terminalStatus) return null;
  return (
    <span
      role="img"
      aria-label={terminalStatus.label}
      className={cn("inline-flex shrink-0 items-center", terminalStatus.colorClass)}
    >
      <TerminalIcon className="size-[calc(var(--acard)*0.875)]" />
    </span>
  );
}

/**
 * Deliberately outside the fading age slot below: it has to stay visible AND
 * clickable while the row is hovered. Only the age label yields to the restore
 * affordance.
 *
 * Grey at rest, the PR's own state colour once you are reading the shelf —
 * history recedes so the live list above it keeps every hue to itself.
 */
function ActivityArchivedRowPrBadge(props: {
  readonly rowProps: ActivityArchivedRowProps;
  readonly state: ActivityArchivedRowState;
}) {
  const badge = resolveActivityCardPrBadge(props.state.pr);
  if (badge === null) return null;
  const hoverColorClass = props.rowProps.isActive
    ? null
    : resolveArchivedRowPrHoverClass(props.state.pr);
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            data-thread-selection-safe
            data-testid={`thread-pr-${props.rowProps.thread.id}`}
            aria-label={badge.tooltip}
            className={cn(
              "shrink-0 cursor-pointer text-[length:calc(var(--acard)*0.75)] leading-[var(--acard)] tabular-nums text-secondary-label transition-colors motion-reduce:transition-none hover:underline focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring",
              hoverColorClass,
            )}
            onPointerDown={props.state.stopPropagationOnPointerDown}
            onClick={props.state.handlePrClick}
          >
            {badge.label}
          </button>
        }
      />
      <TooltipPopup side="top">{badge.tooltip}</TooltipPopup>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// ActivityArchivedRow
// ---------------------------------------------------------------------------

export function ActivityArchivedRow(props: ActivityArchivedRowProps) {
  const state = useSidebarThreadRow(props);
  // Same emphasis rules as the card, not a hard-coded "always dim". An archived
  // thread usually reports no status at all and therefore recedes, but one
  // archived straight out of a finished run still reads as unread, and the
  // title ramp above uses that.
  const emphasis = resolveActivityCardEmphasis({
    threadStatus: state.threadStatus,
    isActive: props.isActive,
    isSelected: state.isSelected,
  });
  const branchMismatch = resolveLocalCheckoutBranchMismatch({
    effectiveEnvMode: props.thread.worktreePath === null ? "local" : "worktree",
    activeWorktreePath: props.thread.worktreePath,
    activeThreadBranch: props.thread.branch,
    currentGitBranch: state.currentGitBranch,
  });

  return (
    <SidebarMenuSubItem
      // `--acard` is declared on the `<li>` rather than on the row so the gap to
      // this row's neighbours is measured on the same scale as everything inside
      // it. The `contain-intrinsic-size` px is a scroll-estimation hint for rows
      // that have not been painted yet, not a layout metric — the row's real
      // height is the `--acard` calc below.
      className="w-full list-none [contain-intrinsic-size:auto_36px] [content-visibility:auto] [--acard:1rem]"
      data-thread-item
      onMouseLeave={state.handleMouseLeave}
      onBlurCapture={state.handleBlurCapture}
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <div
              role="button"
              tabIndex={0}
              data-testid={`thread-row-${props.thread.id}`}
              data-activity-archived-row
              className={cn(
                resolveActivityRowSurfaceClassName({
                  isActive: props.isActive,
                  isSelected: state.isSelected,
                  emphasis,
                }),
                "flex h-[calc(var(--acard)*2.25)] items-center gap-[calc(var(--acard)*0.625)] px-[calc(var(--acard)*0.625)] text-[length:var(--acard)]",
              )}
              onClick={state.handleRowClick}
              onKeyDown={state.handleRowKeyDown}
              onContextMenu={state.handleRowContextMenu}
            />
          }
        >
          {/* History recedes: the project icon is dimmed and desaturated at
              rest and restored on hover, so the shelf stays scannable when you
              are hunting through it without shouting while you are not. */}
          <span
            className={cn(
              "shrink-0 transition-opacity motion-reduce:transition-none",
              props.isActive
                ? null
                : "opacity-40 grayscale group-hover/menu-sub-item:opacity-100 group-hover/menu-sub-item:grayscale-0",
            )}
          >
            <ArchivedRowProjectMark rowProps={props} />
          </span>
          <ActivityArchivedRowTitle rowProps={props} state={state} emphasis={emphasis} />
          <ActivityArchivedRowTerminalGlyph state={state} />
          <ActivityArchivedRowPrBadge rowProps={props} state={state} />
          <ActivityArchivedRowAgeSlot rowProps={props} state={state} />
          {/* Last child of the row surface, which is the `relative` box the pill
              positions against. Outside it the hint anchors to the list. */}
          <SidebarThreadJumpHint label={props.jumpLabel} threadId={props.thread.id} />
        </TooltipTrigger>
        <ActivityThreadCardTooltip
          thread={props.thread}
          projectTitle={props.projectName}
          projectCwd={props.projectCwd}
          environmentLabel={props.environmentLabel ?? null}
          displayBranch={state.displayBranch}
          branchMismatch={branchMismatch}
          terminalStatus={state.terminalStatus}
          terminalProcessCount={state.runningTerminalCount}
        />
      </Tooltip>
    </SidebarMenuSubItem>
  );
}
